import React from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import ShippingProgress from "@/components/ShippingProgress";
import { X, Trash2, Plus, Minus, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const CartDrawer = () => {
    const {
        items,
        isOpen,
        setIsOpen,
        updateQuantity,
        removeItem,
        subtotal,
        totalCount,
    } = useCart();
    const navigate = useNavigate();

    const handleViewCart = () => {
        setIsOpen(false);
        navigate("/cart");
    };

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={() => setIsOpen(false)}
                className={`fixed inset-0 bg-black/70 z-50 transition-opacity duration-300 ${
                    isOpen
                        ? "opacity-100 pointer-events-auto"
                        : "opacity-0 pointer-events-none"
                }`}
                data-testid="cart-drawer-backdrop"
            />
            {/* Drawer */}
            <aside
                data-testid="cart-drawer"
                className={`fixed top-0 right-0 z-50 w-full sm:w-[440px] h-full bg-[#0B0E14] border-l border-[#1E293B] transform transition-transform duration-300 ease-out flex flex-col ${
                    isOpen ? "translate-x-0" : "translate-x-full"
                }`}
            >
                <div className="flex items-center justify-between p-6 border-b border-[#1E293B]">
                    <div>
                        <div className="text-[#ffffff] font-chivo text-[11px] tracking-[0.3em] uppercase">
                            Your Order
                        </div>
                        <div className="font-anton text-2xl uppercase text-white tracking-wider">
                            Standing By ({totalCount})
                        </div>
                    </div>
                    <button
                        data-testid="close-cart-button"
                        onClick={() => setIsOpen(false)}
                        className="p-2 border border-[#1E293B] hover:border-[#ffffff] transition-colors"
                        aria-label="Close cart"
                    >
                        <X className="w-5 h-5 text-white" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {items.length === 0 ? (
                        <div
                            data-testid="empty-cart-message"
                            className="text-center py-20"
                        >
                            <div className="font-anton text-3xl uppercase text-white tracking-wider mb-2">
                                Cart is Empty
                            </div>
                            <p className="font-body text-sm text-slate-400 mb-8">
                                No coffee in the ranks. Reinforcements awaiting.
                            </p>
                            <button
                                onClick={() => setIsOpen(false)}
                                data-testid="continue-shopping-button"
                                className="btn-secondary"
                            >
                                Browse Roasts
                            </button>
                        </div>
                    ) : (
                        <ul className="flex flex-col gap-4">
                            {items.map((item) => (
                                <li
                                    key={item.variant_id}
                                    data-testid={`cart-item-${item.variant_id}`}
                                    className="flex gap-4 border border-[#1E293B] p-3 bg-[#151A22]"
                                >
                                    <div className="w-20 h-20 flex-shrink-0 overflow-hidden border border-[#1E293B]">
                                        <img
                                            src={item.image}
                                            alt={item.name}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-chivo text-[10px] tracking-[0.24em] uppercase text-[#ffffff]">
                                            {item.badge}
                                        </div>
                                        <div className="font-anton text-lg uppercase text-white tracking-wide leading-tight truncate">
                                            {item.name}
                                        </div>
                                        <div className="font-body text-xs text-slate-400 mt-0.5">
                                            {item.grind} · {item.size}
                                        </div>
                                        <div className="flex items-center justify-between mt-3">
                                            <div className="flex items-center border border-[#1E293B]">
                                                <button
                                                    data-testid={`decrease-qty-${item.variant_id}`}
                                                    onClick={() =>
                                                        updateQuantity(
                                                            item.variant_id,
                                                            item.quantity - 1,
                                                        )
                                                    }
                                                    className="p-1.5 hover:bg-[#1E2530] text-white"
                                                    aria-label="Decrease"
                                                >
                                                    <Minus className="w-3.5 h-3.5" />
                                                </button>
                                                <span
                                                    data-testid={`qty-${item.variant_id}`}
                                                    className="px-3 font-chivo text-sm text-white"
                                                >
                                                    {item.quantity}
                                                </span>
                                                <button
                                                    data-testid={`increase-qty-${item.variant_id}`}
                                                    onClick={() =>
                                                        updateQuantity(
                                                            item.variant_id,
                                                            item.quantity + 1,
                                                        )
                                                    }
                                                    className="p-1.5 hover:bg-[#1E2530] text-white"
                                                    aria-label="Increase"
                                                >
                                                    <Plus className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="font-chivo font-bold text-white">
                                                    $
                                                    {(
                                                        item.price *
                                                        item.quantity
                                                    ).toFixed(2)}
                                                </span>
                                                <button
                                                    data-testid={`remove-${item.variant_id}`}
                                                    onClick={() =>
                                                        removeItem(
                                                            item.variant_id,
                                                        )
                                                    }
                                                    className="text-slate-500 hover:text-[#004466] transition-colors"
                                                    aria-label="Remove"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {items.length > 0 && (
                    <div className="border-t border-[#1E293B] p-6 bg-[#0B0E14]">
                        <div className="flex items-center justify-between mb-1">
                            <span className="font-chivo text-[11px] tracking-[0.24em] uppercase text-slate-400">
                                Subtotal
                            </span>
                            <span
                                data-testid="cart-subtotal"
                                className="font-anton text-2xl uppercase text-white"
                            >
                                ${subtotal.toFixed(2)}
                            </span>
                        </div>
                        <p className="font-body text-xs text-slate-500 mb-2">
                            Shipping & taxes calculated at checkout.
                        </p>
                        <ShippingProgress subtotal={subtotal} compact />
                        <div className="mt-4">
                            <button
                                data-testid="view-cart-button"
                                onClick={handleViewCart}
                                className="btn-primary w-full justify-center"
                            >
                                View Cart & Checkout
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </aside>
        </>
    );
};

export default CartDrawer;
